import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Star } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
  { label: "Services", id: "services", href: "https://shale.ink" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-md border-b border-line" : "bg-transparent"
      }`}
    >
      <a
        href="https://share.google/alK7xYjbe4TUkQ9eC"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="header-reviews-badge"
        className="group flex bg-espresso text-cream"
      >
        <div className="max-w-7xl mx-auto w-full px-6 lg:px-10 h-9 flex items-center justify-center gap-2">
          <span className="flex gap-0.5" aria-label="5 out of 5 stars">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} size={12} className="text-gold" fill="currentColor" strokeWidth={0} />
            ))}
          </span>
          <span className="text-xs font-semibold">5.0</span>
          <span className="text-xs text-cream/70 group-hover:text-cream transition-colors">from 110 Google reviews</span>
        </div>
      </a>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" aria-label="Beauté Solutions home">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) =>
            l.href ? (
              <a
                key={l.id}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-link-${l.id}`}
                className="relative text-sm font-semibold text-ink/75 transition-colors duration-300 hover:text-gold"
              >
                {l.label}
              </a>
            ) : (
              <span
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                className="relative text-sm font-semibold text-ink/75"
              >
                {l.label}
              </span>
            )
          )}
          <div className="flex items-center gap-2.5" data-testid="header-cta-group">
            <a
              href="https://beautesolutions.intakeq.com/booking"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-btn"
              className="inline-flex items-center rounded-full bg-gold text-espresso px-6 py-2.5 text-sm font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
            >
              Schedule
            </a>
            <a
              href="https://beautesolutions.intakeq.com/portal"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-portal-btn"
              className="inline-flex items-center rounded-full bg-gold text-espresso px-6 py-2.5 text-sm font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
            >
              My Portal
            </a>
            <a
              href="https://janeiredaleshop.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-shop-btn"
              className="inline-flex items-center rounded-full bg-gold text-espresso px-6 py-2.5 text-sm font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
            >
              Shop
            </a>
          </div>
        </nav>

        <button
          className="md:hidden p-2 text-ink"
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-md border-b border-line"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) =>
                l.href ? (
                  <a
                    key={l.id}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-link-${l.id}`}
                    onClick={() => setOpen(false)}
                    className="font-serif italic text-2xl text-ink"
                  >
                    {l.label}
                  </a>
                ) : (
                  <span
                    key={l.id}
                    data-testid={`mobile-link-${l.id}`}
                    className="font-serif italic text-2xl text-ink"
                  >
                    {l.label}
                  </span>
                )
              )}
              <div className="flex flex-col gap-3 mt-2">
                <a
                  href="https://beautesolutions.intakeq.com/booking"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-book-btn"
                  className="inline-flex items-center justify-center rounded-full bg-gold text-espresso px-5 py-3.5 text-sm font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
                >
                  Schedule
                </a>
                <a
                  href="https://beautesolutions.intakeq.com/portal"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-portal-btn"
                  className="inline-flex items-center justify-center rounded-full bg-gold text-espresso px-5 py-3.5 text-sm font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
                >
                  My Portal
                </a>
                <a
                  href="https://janeiredaleshop.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-shop-btn"
                  className="inline-flex items-center justify-center rounded-full bg-gold text-espresso px-5 py-3.5 text-sm font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
                >
                  Shop
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
